import React, { createContext, ReactNode, useContext } from 'react';

export interface HighlightTerms {
    terms: string[];
    className: string;
    tooltip?: string;
}

interface HighlightContextType {
    pushHighlightTerms: (terms: HighlightTerms[]) => () => void;
    getHighlightTerms: () => HighlightTerms[];
}

const HighlightContext = createContext<HighlightContextType | undefined>(undefined);

interface HighlightProviderProps {
    children?: ReactNode;
}

export const HighlightProvider = ({ children }: HighlightProviderProps): JSX.Element => {
    const [highlightStack, setHighlightStack] = React.useState<Array<{ id: number; terms: HighlightTerms[] }>>([]);
    const nextIdRef = React.useRef(0);

    const pushHighlightTerms = React.useCallback((terms: HighlightTerms[]) => {
        const id = nextIdRef.current;
        nextIdRef.current += 1;
        setHighlightStack((stack) => [...stack, { id, terms }]);
        return () => {
            setHighlightStack((stack) => stack.filter((entry) => entry.id !== id));
        };
    }, []);

    const getHighlightTerms = React.useCallback(() => {
        const currentEntry = highlightStack[highlightStack.length - 1];
        return currentEntry?.terms ?? [];
    }, [highlightStack]);

    const contextValue = React.useMemo(
        () => ({ pushHighlightTerms, getHighlightTerms }),
        [pushHighlightTerms, getHighlightTerms]
    );

    return <HighlightContext.Provider value={contextValue}>{children}</HighlightContext.Provider>;
};

export const useHighlight = (): HighlightContextType => {
    const context = useContext(HighlightContext);

    if (!context) {
        throw new Error('useHighlight must be used within a HighlightProvider');
    }

    return context;
};
